// Layout защищённого поддерева: единственное место проверки роли.
//
// Сессию проверяет beforeLoad самого роута (guards.requireAuth), роль — этот компонент.
// Разделение намеренное: редирект неавторизованного обязан случиться ДО рендера,
// иначе экран мигает содержимым, которого человек видеть не должен. А отказ по роли,
// наоборот, обязан быть видимым — поэтому он рендерится, а не редиректит.

import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState, type ReactElement } from 'react';

import { UpdateBanner } from '@/shared/appUpdate';
import { clearToken, getToken, isSupportSession, useSupportSession } from '@/shared/auth';
import { useSyncEngine } from '@/shared/offline';
import { SessionRejectedError, useSession } from '@/shared/session';
import { PageActionSlotProvider } from '@/shared/ui';

import { KioskNav } from '@/modules/kiosk';
import { useAlertFeedWatcher, usePushSync } from '@/modules/notifications';
import { getViewMode, reflect } from '@/shared/storage';

import { AppNav } from './AppNav';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { SyncStatus } from './SyncStatus';
import { requireAccess, type RouteAccessMeta } from './guards';
import styles from './appLayout.module.css';

// Баннер саппорт-сессии — ЛЕНИВО и только при идущей сессии (T-039). Статический
// импорт модуля платформы тянул в основной бандл все её экраны вместе с библиотекой
// графиков: +113 КБ gzip на каждом кухонном планшете ради баннера, который там
// не показывается никогда. Теперь модуль скачивает только платформенный админ.
const SupportBanner = lazy(() =>
  import('@/modules/platform').then((module) => ({ default: module.SupportBanner })),
);

export function AppLayout(): ReactElement {
  const queryClient = useQueryClient();
  // Узел-приёмник для действия страницы: его отдаёт `AppNav`, а наполняет `PageHeader`.
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const matches = useMatches();

  // Профиль подтягивается один раз и живёт в кэше: guard читает именно его,
  // в том числе офлайн.
  const session = useSession();
  const sessionError = session.error;

  // Двигатель очереди монтируется ЗДЕСЬ и только здесь: очередь общая для приложения,
  // и владелец у неё обязан быть общий. Раньше он жил на экране печати, и всё,
  // поставленное в очередь с дашборда или кухонного табло, не уезжало вовсе.
  //
  // Состояние двигателя больше не выбрасывается (B-023): нештатные исходы очереди —
  // ошибка отправки, операции чужого автора — видны на любом экране, а не только
  // на экране печати.
  const sync = useSyncEngine();

  // Push этого телефона — по выбору вошедшего (B-039): при каждом входе и запуске адрес
  // установки подключается к его сессии или снимается с прежнего владельца. Здесь, а не
  // в экранах входа: все три способа входа и старт приложения ведут сюда.
  usePushSync();

  // Лента предупреждений основной точки (T-047): опрос раз в минуту, счётчик
  // на колокольчике и уведомления Windows на устройстве, где их включили. Здесь — потому
  // что смонтирован ровно при живой сессии: на экране карточек без входа опроса нет.
  useAlertFeedWatcher();

  const navigate = useNavigate();
  useEffect(() => {
    // Токен стирается ТОЛЬКО по прямому отказу сервера субъекту (401/403): сессия
    // мертва на стороне сервера, и держать её локально значит вечно пускать человека
    // в приложение, которое не может загрузить ни одного экрана.
    //
    // Раньше условием был `session.isError`, а он поднимается и после двух подряд
    // NetworkError — то есть после обычного обрыва WiFi на кухне. Отличить одно
    // от другого по `navigator.onLine` нельзя, он в этом же репозитории признан
    // врущим (useSync.ts). Цена ошибки несимметрична: сеть вернётся сама, а стёртый
    // токен без сети не восстановить — войти офлайн невозможно (offline-first.md §4).
    // Сегодня обрыв стоил кабинета; когда печать переедет на человеческую сессию,
    // он будет стоить самой печати — ровно в тот момент, ради которого офлайн-first
    // и существует (риск Р2, docs/phase_2/_auth-model-v1.2-decisions.md §4).
    //
    // 403 приравнен к 401: DRF отвечает 401 только при наличии WWW-Authenticate,
    // и «субъекта нет» штатно приходит третьим кодом.
    if (!(sessionError instanceof SessionRejectedError)) return;

    // Отказ обязан быть адресован ТЕКУЩЕМУ токену (B-034). Кэш запросов переживает
    // и выход на экран входа, и повторный вход, а вместе с ним — отказ прошлой сессии:
    // первый же рендер после входа видел его и стирал уже новый токен. Человека
    // выкидывало на следующем переходе, и так до перезапуска приложения.
    if (sessionError.token === null || sessionError.token !== getToken()) return;

    // В саппорт-сессии отказ относится к ЧУЖОМУ токену: `clearToken` только вернёт
    // платформу в её собственную сессию, а кэш сбросит эффект смены сессии ниже.
    if (isSupportSession()) {
      void clearToken();
      return;
    }

    // На вход — сразу и с причиной. Раньше токен стирался молча: экран оставался
    // открытым до следующего нажатия, а на холодном старте не рисовалась даже
    // навигация — уйти было некуда. Очередь непросинканных операций при этом
    // не трогается (offline-first.md §5, п. 10): её отправит следующий вход.
    void clearToken();
    void navigate({ to: '/login', search: { reason: sessionError.reason } });
  }, [sessionError, navigate]);

  // Саппорт-сессия началась или кончилась — кэш запросов принадлежит ДРУГОМУ субъекту
  // и сбрасывается целиком (T-039). Здесь, а не только в кнопках входа и выхода:
  // сессия кончается и отказом сервера (401 на чужом токене → `clearToken`), и тогда
  // экран показывал бы данные клиента уже под токеном платформы.
  //
  // Только на СМЕНЕ, не при монтировании: иначе каждый старт стирал бы кэш, поднятый
  // с диска для работы офлайн.
  const support = useSupportSession();
  const supportKey = support?.banner ?? '';
  const previousSupportKey = useRef(supportKey);
  useEffect(() => {
    if (previousSupportKey.current === supportKey) return;
    previousSupportKey.current = supportKey;
    queryClient.clear();
  }, [supportKey, queryClient]);

  // Мета берётся с САМОГО ГЛУБОКОГО совпавшего роута: вложенный экран может быть
  // строже родителя, и побеждать должен он.
  const meta = [...matches]
    .reverse()
    .map((match) => match.staticData as RouteAccessMeta | undefined)
    .find((data) => data?.allowedRoles !== undefined);

  const forbidden = Boolean(meta) && !requireAccess(queryClient, meta as RouteAccessMeta);

  // Сотрудник живёт ТОЛЬКО в киоске (T-019, решение владельца от 2026-07-30).
  //
  // Не «по умолчанию», а всегда: режим отображения для него не переключаемая настройка,
  // а свойство роли. Кабинетный вид повару не нужен — его работа это печать и табло,
  // а оба экрана есть в киоске, — и вкладки кабинета для него были дублированием
  // с худшей плотностью: compact-строки менеджера на кухонной дистанции не читаются.
  //
  // Проверяется здесь, а не в `getViewMode`: хранилище отвечает на вопрос «что человек
  // выбрал», а это правило — «что ему положено». Слить их значило бы записать роль
  // в localStorage, откуда её можно поправить руками.
  const kioskOnly = session.data?.role === 'employee';
  const kioskView = kioskOnly || getViewMode() === 'kiosk';

  // Какой хром сейчас на экране — на корне документа, чтобы плавающие слои
  // (тосты) знали, что им не перекрывать.
  //
  // Через атрибут, а не через контекст: тост рендерится глубоко в дереве и позиционируется
  // фиксированно, то есть вне потока и вне любого провайдера над ним. Атрибут на корне —
  // единственное, что видно и CSS, и с любой глубины.
  useEffect(() => {
    // Отражается ДЕЙСТВУЮЩИЙ вид, а не сохранённое предпочтение. Для сотрудника это
    // разные вещи: он в киоске по роли и ничего не переключал, поэтому в хранилище
    // у него так и лежит `cabinet`. Пока атрибуты ставило только хранилище, повар
    // получал плотность менеджера — 13px и 44px тап-цели в перчатках у плиты.
    reflect(kioskView ? 'kiosk' : 'cabinet');
    document.documentElement.dataset.chrome = kioskView ? 'kiosk' : 'cabinet';
  }, [kioskView]);

  return (
    <>
      {/* Несъёмный баннер саппорт-сессии — над всем, на любом экране (T-039). */}
      {support ? (
        <Suspense fallback={null}>
          <SupportBanner />
        </Suspense>
      ) : null}

      {/* Верхний хром — навигация и под ней строки состояния: нештатное в очереди синка
          и «доступна новая версия» — липнет к верху ОДНИМ блоком (appLayout.module.css). */}
      <div className={styles.top}>
        {/* Навигация рисуется ТОЛЬКО когда профиль загружен: до него неизвестна роль,
            а набор пунктов от неё зависит. Мигнуть чужим меню и убрать его хуже,
            чем показать меню на полсекунды позже.

            Какая именно панель — решает РЕЖИМ ОТОБРАЖЕНИЯ, а не роль и не маршрут.
            Роль отвечает на «что тебе можно», режим — на «как это показать»; смешать их
            значит получить владельца сети, которому кухонный экран показать нечем. */}
        {session.data && !kioskView ? (
          <AppNav user={session.data} onActionSlot={setActionSlot} />
        ) : null}

        {/* Навигация вида киоска — тоже ШАПКА СВЕРХУ (T-019 → пересмотр 2026-09-02):
            нижняя панель на телефоне менеджера читалась чужой, а выход из вида прятался
            за именем в углу, которое CSS на узком экране скрывал. Один layout на оба
            вида: слева бургер, под ним разделы и выход. */}
        {session.data && kioskView ? <KioskNav onActionSlot={setActionSlot} /> : null}

        {/* Ошибка отправки и «нужен вход» — строкой ПОД навигацией, а не слоем у нижней
            кромки: внизу у экранов липкие футеры с основной кнопкой (почему —
            в SyncStatus.tsx). Выше строки обновления: за ней данные, которые не дошли
            до сервера и ждут человека, а обновление подождёт удобной минуты. */}
        <SyncStatus state={sync} />

        {/* Баннер «доступна новая версия» — строкой ПОД навигацией, а не слоем поверх
            экрана (почему — в UpdateBanner.tsx). Монтируется здесь, рядом с двигателем
            очереди синка и по той же причине: проверка обновления — приложение-широкая
            забота с одним владельцем, а виден баннер на любом экране. */}
        <UpdateBanner />
      </div>

      {/* Отказ по роли подменяет СОДЕРЖИМОЕ, а не весь экран. Раньше здесь стоял
          ранний `return`, и вместе с разделом исчезала навигация: человек оставался
          на странице, с которой некуда нажать. В вебе он правил адрес руками,
          а в оболочке на устройстве точки адресной строки нет вовсе — выхода
          не было до перезапуска приложения. */}
      <PageActionSlotProvider slot={actionSlot}>
        {forbidden ? <ForbiddenPage /> : <Outlet />}
      </PageActionSlotProvider>
    </>
  );
}
